import { ArrowDownTrayIcon, ClipboardDocumentIcon, PrinterIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, LinkButton, notify } from '../../../components/ui';
import type { LocationRecord } from '../apiTypes';
import { PRINT_TEXT, pngCard } from '../printText';
import { downloadPng, downloadSvg, fileName, locationUrl } from '../qr';
import { QrView } from './QrView';

export const LocationCodeCard = ({ location }: { location: LocationRecord }) => {
    const { id: locationId, name: locationName } = location;
    const { t } = useTranslation('locations');
    const url = locationUrl(locationId);
    const [busy, setBusy] = useState<'png' | 'svg' | null>(null);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            notify.success(t('qr.copied'));
        } catch {
            notify.error(t('qr.copyFailed'));
        }
    };
    const download = async (kind: 'png' | 'svg') => {
        setBusy(kind);
        try {
            if (kind === 'png')
                await downloadPng(
                    url,
                    fileName(locationName, 'png', 'to-go'),
                    pngCard(location, (l) => PRINT_TEXT[l]?.togo)
                );
            else await downloadSvg(url, fileName(locationName, 'svg', 'to-go'));
        } catch {
            notify.error(t('qr.downloadFailed'));
        } finally {
            setBusy(null);
        }
    };

    return (
        <section aria-labelledby="code-heading">
            <Card padding="lg" className="flex flex-col gap-5 sm:flex-row">
                <QrView
                    url={url}
                    size={400}
                    label={t('qr.locationAlt', { name: locationName })}
                    className="size-[200px] shrink-0 self-center rounded-control shadow-hairline sm:self-start"
                />
                <div className="min-w-0">
                    <h2 id="code-heading" className="text-title text-strong">
                        {t('qr.locationTitle')}
                    </h2>
                    <p className="mt-1 text-small text-muted">{t('qr.locationBody')}</p>
                    <p className="mt-3 break-all text-small text-body">
                        <span className="sr-only">{t('qr.link')}: </span>
                        {url}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <Button variant="outline" onClick={copy} icon={<ClipboardDocumentIcon className="size-5" aria-hidden="true" />}>
                            {t('qr.copy')}
                        </Button>
                        <LinkButton to={`/locations/${locationId}/qr/print`} variant="outline" icon={<PrinterIcon className="size-5" aria-hidden="true" />}>
                            {t('qr.print')}
                        </LinkButton>
                        <Button
                            variant="outline"
                            loading={busy === 'png'}
                            disabled={busy !== null}
                            onClick={() => download('png')}
                            icon={<ArrowDownTrayIcon className="size-5" aria-hidden="true" />}
                        >
                            {t('qr.png')}
                        </Button>
                        <Button
                            variant="outline"
                            loading={busy === 'svg'}
                            disabled={busy !== null}
                            onClick={() => download('svg')}
                            icon={<ArrowDownTrayIcon className="size-5" aria-hidden="true" />}
                        >
                            {t('qr.svg')}
                        </Button>
                    </div>
                    <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-4 inline-flex min-h-control items-center text-small font-semibold text-link underline underline-offset-2"
                    >
                        {t('qr.open')}
                        <span className="sr-only"> {t('qr.newTab')}</span>
                    </a>
                </div>
            </Card>
        </section>
    );
};
